import { Localizable } from '@components/localizable/Localizable'
import { LegalDocumentVersion as LegalDocumentVersionModel } from '@models/LegalDocument'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { selectLegalDocumentBySlug } from '@store/LegalDocumentReducer'
import { createLegalDocumentVersion } from '@store/LegalDocumentVersionReducer'
import { SBModelComponent } from '@supplement-bacon/react-toolkit/models'
import { requiredRule } from '@utils/rules'
import { App, Button, Card, Flex, Form } from 'antd'
import { FC, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { DEFAULT_ERROR, DEFAULT_SUCCESS } from '../../App'

type Props = SBModelComponent<LegalDocumentVersionModel> & {
  onRefresh: () => void
  isBlank: boolean
}

const LegalDocumentVersionNew: FC<Props> = ({ onRefresh, isBlank }) => {
  const { legalDocumentSlug } = useParams()
  const navigate = useNavigate()
  const { message } = App.useApp()
  const [form] = Form.useForm()
  const dispatch = useAppDispatch()
  const legalDocument = useAppSelector(
    selectLegalDocumentBySlug(legalDocumentSlug ?? '')
  )
  const item = legalDocument?.activeVersion

  useEffect(() => {
    if (!item) return

    if (isBlank) {
      form.resetFields()
      return
    }

    form.setFieldsValue({ ...item })
  }, [item, isBlank])

  if (!legalDocumentSlug || !legalDocument) return

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      const createdVersion = await dispatch(
        createLegalDocumentVersion(legalDocumentSlug, values)
      )

      message.success(DEFAULT_SUCCESS)

      onRefresh()

      form.resetFields()

      if (createdVersion?.version !== undefined) {
        navigate(String(createdVersion.version))
      }
    } catch {
      message.error(DEFAULT_ERROR)
    }
  }

  return (
    <Card
      title={'New version'}
      extra={
        <Button type="primary" onClick={handleSubmit}>
          Save and create version
        </Button>
      }
    >
      <Flex justify="end">
        <Localizable.LangSelector />
      </Flex>

      <Form form={form} layout="vertical">
        <Localizable.Input
          name={'title'}
          label="Title"
          rules={[requiredRule()]}
        />

        <Localizable.TextEditor
          name={'body'}
          label="Content"
          rules={[requiredRule()]}
          mode="full"
        />
      </Form>
    </Card>
  )
}

type LegalDocumentVersionNewType = {
  New: typeof LegalDocumentVersionNew
}

export { LegalDocumentVersionNew, type LegalDocumentVersionNewType }
